import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AdminLayout from "@/components/AdminLayout";
import axios from "axios";
import { toast } from "sonner";
import { Activity, CheckCircle, XCircle, Clock, Search, Eye, TrendingUp, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { can } from "@/lib/permissions";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ITEMS_PER_PAGE = 50;

const APILogs = ({ user, onLogout }) => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);

  // Retention (admin-set cutoff + purge)
  const canPurge = can(user, "api_logs:delete");
  const [showRetention, setShowRetention] = useState(false);
  const [retentionDays, setRetentionDays] = useState("");
  const [retentionConfig, setRetentionConfig] = useState(null);
  const [purging, setPurging] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [filterProvider, setFilterProvider] = useState("all");
  const [filterSuccess, setFilterSuccess] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFromDate, setAppliedFromDate] = useState("");
  const [appliedToDate, setAppliedToDate] = useState("");
  const [filterNotes, setFilterNotes] = useState("");
  const [notesSearch, setNotesSearch] = useState("");
  const [filterOptions, setFilterOptions] = useState({ endpoints: [], notes: [] });

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchQuery), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [filterProvider, filterSuccess, currentPage, searchDebounced, appliedFromDate, appliedToDate, filterNotes]);

  useEffect(() => { fetchFilterOptions(); }, []);

  const fetchFilterOptions = async () => {
    try {
      const response = await axios.get(`${API}/api-logs/filter-values/options`);
      setFilterOptions(response.data);
    } catch {}
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', ITEMS_PER_PAGE);
      params.append('skip', (currentPage - 1) * ITEMS_PER_PAGE);
      if (filterProvider && filterProvider !== "all") params.append('api_provider', filterProvider);
      if (filterSuccess && filterSuccess !== "all") params.append('is_success', filterSuccess);
      if (searchDebounced) params.append('search', searchDebounced);
      if (appliedFromDate) params.append('from_date', appliedFromDate);
      if (appliedToDate) params.append('to_date', appliedToDate);
      if (filterNotes && filterNotes !== "all") params.append('notes', filterNotes);
      
      const response = await axios.get(`${API}/api-logs/?${params.toString()}`);
      setLogs(response.data);
      
      // Get total count from stats for pagination
      if (stats) {
        let total = stats.total_logs;
        // Adjust total based on filters
        if (filterProvider !== "all" || filterSuccess !== "all") {
          // Use response length as approximation when filtered
          // This is acceptable since backend returns up to limit
          total = response.data.length < ITEMS_PER_PAGE ? 
            (currentPage - 1) * ITEMS_PER_PAGE + response.data.length : 
            stats.total_logs;
        }
        setTotalLogs(total);
        setTotalPages(Math.max(1, Math.ceil(total / ITEMS_PER_PAGE)));
      }
    } catch (error) {
      console.error("Failed to fetch API logs:", error);
      if (error.response && error.response.status !== 404) {
        toast.error("Failed to fetch API logs");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api-logs/stats/summary`);
      setStats(response.data);
      setTotalLogs(response.data.total_logs);
      setTotalPages(Math.max(1, Math.ceil(response.data.total_logs / ITEMS_PER_PAGE)));
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const openDetailDialog = (log) => {
    setSelectedLog(log);
    setShowDetailDialog(true);
  };

  // Reset to page 1 when filters change
  const handleFilterChange = (setter, value) => {
    setter(value);
    setCurrentPage(1);
  };

  useEffect(() => { setCurrentPage(1); }, [searchDebounced]);

  const filteredLogs = logs;

  // Calculate showing range
  const startItem = (currentPage - 1) * ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * ITEMS_PER_PAGE, totalLogs);

  const fetchRetention = async () => {
    try {
      const res = await axios.get(`${API}/api-logs/retention/config`);
      setRetentionConfig(res.data);
      if (res.data?.retention_days) setRetentionDays(String(res.data.retention_days));
    } catch (e) { /* retention is optional; ignore */ }
  };

  useEffect(() => { if (canPurge) fetchRetention(); }, [canPurge]);

  const handlePurge = async () => {
    const days = parseInt(retentionDays, 10);
    if (!Number.isFinite(days) || days < 1) {
      toast.error("Enter a whole number of days (at least 1)");
      return;
    }
    if (!window.confirm(`Delete ALL API logs older than ${days} days? This cannot be undone.`)) return;
    setPurging(true);
    try {
      const res = await axios.post(`${API}/api-logs/retention/purge`, { days });
      toast.success(`Deleted ${res.data?.deleted ?? 0} logs older than ${res.data?.retention_days} days`);
      setShowRetention(false);
      fetchRetention();
      fetchLogs();
      fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to purge logs");
    } finally {
      setPurging(false);
    }
  };

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">API Logs</h1>
            <p className="text-slate-600">Monitor third-party API integration calls</p>
          </div>
          {canPurge && (
            <Button variant="outline" onClick={() => setShowRetention(true)} data-testid="api-logs-retention-button">
              <Trash2 className="w-4 h-4 mr-2" /> Retention
            </Button>
          )}
        </div>

        {/* Retention dialog: set the cutoff (days) and delete older logs */}
        <Dialog open={showRetention} onOpenChange={setShowRetention}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>API log retention</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Delete API logs older than the number of days below. Older logs are
                permanently removed.
                {retentionConfig?.retention_days
                  ? ` Current setting: ${retentionConfig.retention_days} days.`
                  : " No cutoff set yet."}
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="retention-days">Delete logs older than (days)</Label>
                <Input
                  id="retention-days"
                  data-testid="retention-days-input"
                  type="number"
                  min="1"
                  value={retentionDays}
                  onChange={(e) => setRetentionDays(e.target.value)}
                  placeholder="e.g. 90"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" onClick={() => setShowRetention(false)}>Cancel</Button>
                <Button
                  onClick={handlePurge}
                  disabled={purging}
                  data-testid="retention-purge-button"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {purging ? "Deleting…" : "Set & delete old logs"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Total Calls</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_logs}</p>
                  </div>
                  <Activity className="w-10 h-10 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Success Rate</p>
                    <p className="text-2xl font-bold text-green-600">{stats.success_rate}%</p>
                  </div>
                  <TrendingUp className="w-10 h-10 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Successful</p>
                    <p className="text-2xl font-bold text-green-600">{stats.success_count}</p>
                  </div>
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600">Avg Response</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.average_response_time_ms}ms</p>
                  </div>
                  <Clock className="w-10 h-10 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Search Order/Endpoint</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>API Provider</Label>
                <Select value={filterProvider} onValueChange={(val) => handleFilterChange(setFilterProvider, val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Providers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Providers</SelectItem>
                    <SelectItem value="Delhivery">Delhivery</SelectItem>
                    <SelectItem value="BlueDart">BlueDart</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={filterSuccess} onValueChange={(val) => handleFilterChange(setFilterSuccess, val)}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="true">Success</SelectItem>
                    <SelectItem value="false">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="space-y-2">
                <Label>Notes</Label>
                <div className="relative">
                  <Input
                    placeholder="Type to search notes..."
                    value={notesSearch}
                    onChange={(e) => setNotesSearch(e.target.value)}
                    className="pr-2"
                  />
                  {notesSearch && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filterOptions.notes
                        .filter((n) => n.toLowerCase().includes(notesSearch.toLowerCase()))
                        .slice(0, 20)
                        .map((n) => (
                          <button
                            key={n}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 truncate"
                            onClick={() => { setFilterNotes(n); setNotesSearch(""); setCurrentPage(1); }}
                          >
                            {n}
                          </button>
                        ))}
                      {filterOptions.notes.filter((n) => n.toLowerCase().includes(notesSearch.toLowerCase())).length === 0 && (
                        <div className="px-3 py-2 text-xs text-slate-400">No matching notes</div>
                      )}
                    </div>
                  )}
                  {filterNotes && !notesSearch && (
                    <div className="flex items-center gap-1 mt-1">
                      <Badge variant="outline" className="text-xs bg-slate-50 max-w-full">
                        <span className="truncate">{filterNotes}</span>
                      </Badge>
                      <button className="text-slate-400 hover:text-slate-600 text-xs" onClick={() => { setFilterNotes(""); setCurrentPage(1); }}>&times;</button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-end gap-4 mt-4">
              <div className="space-y-2">
                <Label>From</Label>
                <Input
                  type="datetime-local"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-52"
                />
              </div>
              <div className="space-y-2">
                <Label>To</Label>
                <Input
                  type="datetime-local"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-52"
                />
              </div>
              <Button
                data-testid="apply-date-filter-btn"
                size="sm"
                className="h-10 px-5"
                style={{ backgroundColor: '#00A699' }}
                disabled={!fromDate && !toDate}
                onClick={() => { setAppliedFromDate(fromDate); setAppliedToDate(toDate); setCurrentPage(1); }}
              >
                Apply
              </Button>
              {(appliedFromDate || appliedToDate) && (
                <Button
                  data-testid="clear-date-filter-btn"
                  size="sm"
                  variant="outline"
                  className="h-10 px-4"
                  onClick={() => { setFromDate(""); setToDate(""); setAppliedFromDate(""); setAppliedToDate(""); setCurrentPage(1); }}
                >
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Logs Table */}
        <Card className="border-0 shadow-lg">
          <CardContent className="p-0">
            {loading ? (
              <div className="text-center py-12">Loading logs...</div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-12 text-slate-500">No API logs found</div>
            ) : (
              <>
                {/* Pagination — above the table so it stays clear of the floating assistant button */}
                <div className="flex flex-col gap-3 px-6 py-4 border-b border-slate-200 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-sm text-slate-600" data-testid="api-logs-range">
                    Showing {startItem} to {endItem} of {totalLogs} logs
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      data-testid="api-logs-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-slate-600 whitespace-nowrap">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      data-testid="api-logs-next-page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Timestamp</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Provider</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Order</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Endpoint</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Status</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Duration</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Triggered By</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredLogs.map((log) => (
                        <tr key={log.id} className="border-b hover:bg-slate-50">
                          <td className="px-4 py-3 align-middle whitespace-nowrap text-sm text-slate-600">
                            <div>{formatToIST(log.created_at, false)}</div>
                            <div className="text-xs text-slate-500">{formatToIST(log.created_at).split(', ')[1]}</div>
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap">
                            <Badge className="bg-blue-100 text-blue-800 border-0">
                              {log.api_provider}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap text-sm font-medium text-slate-900">
                            {log.order_number || '-'}
                          </td>
                          <td className="px-4 py-3 align-middle text-sm text-slate-600 max-w-xs truncate">
                            {log.api_endpoint}
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap">
                            {log.is_success ? (
                              <Badge className="bg-green-100 text-green-800 border-0">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Success
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800 border-0">
                                <XCircle className="w-3 h-3 mr-1" />
                                Failed
                              </Badge>
                            )}
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap text-sm text-slate-600 text-right">
                            {log.duration_ms ? `${log.duration_ms.toFixed(0)}ms` : '-'}
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap text-sm text-slate-600">
                            <div>{log.triggered_by}</div>
                            <div className="text-xs text-slate-400">{log.triggered_by_role}</div>
                          </td>
                          <td className="px-4 py-3 align-middle whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openDetailDialog(log)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>API Log Details</DialogTitle>
            </DialogHeader>
            {selectedLog && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Provider</Label>
                    <p className="font-medium">{selectedLog.api_provider}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Order Number</Label>
                    <p className="font-medium">{selectedLog.order_number || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">HTTP Method</Label>
                    <p className="font-medium">{selectedLog.http_method}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Status Code</Label>
                    <p className="font-medium">{selectedLog.status_code}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Duration</Label>
                    <p className="font-medium">{selectedLog.duration_ms?.toFixed(2)}ms</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Status</Label>
                    {selectedLog.is_success ? (
                      <Badge className="bg-green-100 text-green-800 border-0">Success</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-800 border-0">Failed</Badge>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Timestamp (IST)</Label>
                    <p className="font-medium">{formatToIST(selectedLog.created_at)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Triggered By</Label>
                    <p className="font-medium">{selectedLog.triggered_by || '-'} ({selectedLog.triggered_by_role || '-'})</p>
                  </div>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Endpoint</Label>
                  <p className="font-mono text-sm bg-slate-50 p-3 rounded break-all">{selectedLog.api_endpoint}</p>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Request Payload</Label>
                  <pre className="font-mono text-xs bg-slate-50 p-4 rounded overflow-x-auto">
                    {JSON.stringify(selectedLog.request_payload, null, 2)}
                  </pre>
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Response Data</Label>
                  <pre className="font-mono text-xs bg-slate-50 p-4 rounded overflow-x-auto max-h-60">
                    {JSON.stringify(selectedLog.response_data, null, 2)}
                  </pre>
                </div>

                {selectedLog.error_message && (
                  <div>
                    <Label className="text-xs text-slate-500">Error Message</Label>
                    <p className="text-sm text-red-600 bg-red-50 p-4 rounded">{selectedLog.error_message}</p>
                  </div>
                )}

                {selectedLog.notes && (
                  <div>
                    <Label className="text-xs text-slate-500">Notes</Label>
                    <p className="text-sm">{selectedLog.notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default APILogs;
